import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchDefinitionOnlyFamilySnapshot } from '../../../shared/lib/revit/definitionOnlyFamilySnapshot';
import type { RevitFamily } from '../../../shared/api/revit/types';
import type { AvailableProjectParameterDescriptor } from '../../../shared/lib/revit/projectParameterMutationPlan';
import { sanitizeRevitParameterNames } from '../../../shared/lib/revitParameterNames';
import type { MappingParameterScopeRequirement } from '../lib/mappingReadinessFamilyExclusions';
import { buildMappingReadinessSnapshotRequest } from '../lib/mappingReadinessSnapshotRequest';
import { traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';
import type { MappingCatalogRefreshSuspensionRef } from './useMappingParameterCatalogs';

interface MappingReadinessSnapshotData {
  parameterNames: string[];
  projectDescriptors: AvailableProjectParameterDescriptor[];
  sampledParameterNames: string[];
  families: RevitFamily[];
}

interface MappingReadinessSnapshotState extends MappingReadinessSnapshotData {
  scopeKey: string;
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
}

const EMPTY_DATA: MappingReadinessSnapshotData = {
  parameterNames: [],
  projectDescriptors: [],
  sampledParameterNames: [],
  families: [],
};
const sharedPendingSnapshots = new Map<string, Promise<MappingReadinessSnapshotData>>();
const sharedSnapshotListeners = new Map<
  string,
  Set<(data: MappingReadinessSnapshotData) => void>
>();
export const __mappingReadinessSnapshotTestInternals = {
  clearPendingRequests: () => {
    sharedPendingSnapshots.clear();
    sharedSnapshotListeners.clear();
  },
};

function publishSnapshot(scopeKey: string, data: MappingReadinessSnapshotData): void {
  sharedSnapshotListeners.get(scopeKey)?.forEach((listener) => listener(data));
}

function mapSnapshotData(result: {
  availableParameters?: any[];
  families?: RevitFamily[];
}): MappingReadinessSnapshotData {
  const parameters = Array.isArray(result.availableParameters)
    ? result.availableParameters
    : [];
  const families = Array.isArray(result.families) ? result.families : [];
  const projectDescriptors = parameters
    .filter((parameter) => typeof parameter?.name === 'string' && parameter.name.trim())
    .map((parameter): AvailableProjectParameterDescriptor => ({
      name: parameter.name.trim(),
      dataType: typeof parameter.dataType === 'string' ? parameter.dataType : '',
      parameterType: parameter.isInstance || parameter.parameterType === 'Instance'
        ? 'Instance'
        : 'Type',
      group: typeof parameter.group === 'string' ? parameter.group : '',
      categories: Array.isArray(parameter.categories)
        ? parameter.categories.filter((category: unknown): category is string =>
            typeof category === 'string'
          )
        : undefined,
      isProjectBinding: typeof parameter.isProjectBinding === 'boolean'
        ? parameter.isProjectBinding
        : undefined,
    }));
  const familyParameterNames = families.flatMap((family) =>
    family.types.flatMap((type) =>
      (type.parameterValues ?? []).map((parameter) => parameter.parameterName)
    )
  );
  const names = sanitizeRevitParameterNames(parameters.flatMap((parameter) => [
    typeof parameter?.name === 'string' ? parameter.name : '',
    typeof parameter?.label === 'string' ? parameter.label : '',
  ]));
  return {
    parameterNames: names,
    projectDescriptors,
    sampledParameterNames: sanitizeRevitParameterNames([...names, ...familyParameterNames]),
    families,
  };
}

export function useMappingReadinessSnapshot(input: {
  enabled: boolean;
  runtimeAvailable: boolean;
  projectKey: string | null;
  modelVersion: number | null;
  evidenceRevision?: string | null;
  requirements: MappingParameterScopeRequirement[];
  refreshSuspendedRef?: MappingCatalogRefreshSuspensionRef;
}) {
  const request = useMemo(
    () => buildMappingReadinessSnapshotRequest(input.requirements),
    [input.requirements]
  );
  const requestKey = useMemo(
    () => JSON.stringify(request),
    [request]
  );
  const scopeKey = input.enabled && input.runtimeAvailable && input.projectKey
    ? `${input.projectKey}::${input.modelVersion ?? 'unknown'}::${input.evidenceRevision ?? 'open'}::${requestKey}`
    : '';
  const [state, setState] = useState<MappingReadinessSnapshotState>({
    scopeKey: '',
    isLoading: false,
    isReady: false,
    error: null,
    ...EMPTY_DATA,
  });
  const sequenceRef = useRef(0);
  const pendingRef = useRef<{ scopeKey: string; request: Promise<MappingReadinessSnapshotData> } | null>(null);

  const readSnapshot = useCallback(async (force = false): Promise<MappingReadinessSnapshotData> => {
    if (!scopeKey || request.parameterNames.length === 0) {
      setState({ scopeKey, isLoading: false, isReady: true, error: null, ...EMPTY_DATA });
      return EMPTY_DATA;
    }
    if (pendingRef.current?.scopeKey === scopeKey) {
      return pendingRef.current.request;
    }

    const sequence = ++sequenceRef.current;
    setState((current) => ({
      ...(current.scopeKey === scopeKey ? current : EMPTY_DATA),
      scopeKey,
      isLoading: true,
      isReady: false,
      error: null,
    }));
    traceMappingFlow('mapping readiness snapshot fetch', {
      projectKey: input.projectKey,
      modelVersion: input.modelVersion,
      requiredNameCount: request.parameterNames.length,
      force,
    });
    let pending = sharedPendingSnapshots.get(scopeKey);
    if (!pending) {
      pending = fetchDefinitionOnlyFamilySnapshot({
        parameterNames: request.parameterNames,
        requestedParameters: request.requestedParameters,
        categoryNames: request.categoryNames,
      }).then((snapshot) => {
        if (!snapshot.available) {
          throw new Error(snapshot.error || 'Mapping parameter inspection failed');
        }
        return mapSnapshotData({
          availableParameters: snapshot.availableParameters,
          families: snapshot.families,
        });
      });
      sharedPendingSnapshots.set(scopeKey, pending);
    }
    pendingRef.current = { scopeKey, request: pending };
    try {
      const data = await pending;
      publishSnapshot(scopeKey, data);
      if (sequence === sequenceRef.current) {
        setState({ scopeKey, isLoading: false, isReady: true, error: null, ...data });
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Mapping parameter inspection failed';
      if (sequence === sequenceRef.current) {
        setState({ scopeKey, isLoading: false, isReady: false, error: message, ...EMPTY_DATA });
      }
      warnMappingFlow('mapping readiness snapshot failed', {
        projectKey: input.projectKey,
        modelVersion: input.modelVersion,
        message,
      });
      throw error;
    } finally {
      if (pendingRef.current?.request === pending) pendingRef.current = null;
      if (sharedPendingSnapshots.get(scopeKey) === pending) sharedPendingSnapshots.delete(scopeKey);
    }
  }, [input.modelVersion, input.projectKey, request, scopeKey]);

  useEffect(() => {
    if (!scopeKey) return;
    const listener = (data: MappingReadinessSnapshotData) => {
      setState({ scopeKey, isLoading: false, isReady: true, error: null, ...data });
    };
    const listeners = sharedSnapshotListeners.get(scopeKey) ?? new Set();
    listeners.add(listener);
    sharedSnapshotListeners.set(scopeKey, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) sharedSnapshotListeners.delete(scopeKey);
    };
  }, [scopeKey]);

  useEffect(() => {
    if (!scopeKey) return;
    if (
      input.refreshSuspendedRef?.current.active &&
      input.refreshSuspendedRef.current.projectKey === input.projectKey
    ) return;
    if (state.scopeKey === scopeKey && (state.isLoading || state.isReady || state.error)) return;
    void readSnapshot().catch(() => undefined);
  }, [input.projectKey, input.refreshSuspendedRef, readSnapshot, scopeKey, state]);

  const matchesScope = state.scopeKey === scopeKey;
  const refresh = useCallback(() => readSnapshot(true), [readSnapshot]);
  return {
    parameterNames: matchesScope ? state.parameterNames : EMPTY_DATA.parameterNames,
    projectDescriptors: matchesScope ? state.projectDescriptors : EMPTY_DATA.projectDescriptors,
    sampledParameterNames: matchesScope
      ? state.sampledParameterNames
      : EMPTY_DATA.sampledParameterNames,
    families: matchesScope ? state.families : EMPTY_DATA.families,
    isLoading: matchesScope && state.isLoading,
    isReady: !scopeKey || (matchesScope && state.isReady),
    error: matchesScope ? state.error : null,
    refresh,
  };
}
